import React from 'react';
import Modal from 'components/ModalPopup';
import { Button, Group, Select } from '@mantine/core';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const filterSchema = z.object({
  year: z.string().optional()
});

type FilterFormData = z.infer<typeof filterSchema>;

interface FilterCourseCombineTNAProps {
  opened: boolean;
  onClose: () => void;
  onSubmitForm: (_data: { year?: number }) => void;
  defaultData?: {
    year?: number;
  };
}

const FilterCourseCombineTNA: React.FC<FilterCourseCombineTNAProps> = ({
  opened,
  onClose,
  onSubmitForm,
  defaultData
}) => {
  const currentYear = new Date().getFullYear();

  // Generate year options: 5 years before to 2 years after current year
  const yearOptions = React.useMemo(() => {
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      years.push({
        value: i.toString(),
        label: i.toString()
      });
    }
    return years.reverse(); // Most recent years first
  }, [currentYear]);

  const { control, handleSubmit, reset } = useForm<FilterFormData>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      year: defaultData?.year?.toString()
    }
  });

  const onSubmit = (data: FilterFormData) => {
    onSubmitForm({
      year: data.year ? parseInt(data.year) : undefined
    });
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleClear = () => {
    reset({ year: undefined });
    onSubmitForm({ year: undefined });
    onClose();
  };

  return (
    <Modal title='Filter Data' size='lg' opened={opened} close={onClose} withCloseButton>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='year'
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              label='Year'
              placeholder='Select year'
              data={yearOptions}
              clearable
              searchable
              mb='md'
            />
          )}
        />
        <Group justify='flex-end' mt={24}>
          <Button variant='outline' onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant='default' onClick={handleClear}>
            Clear
          </Button>
          <Button type='submit'>Filter</Button>
        </Group>
      </form>
    </Modal>
  );
};

export default FilterCourseCombineTNA;
